/**
 * Adapts the `/api/recipes/:id` detail payload into the chefs `Recipe`
 * shape that the shared recipe renderers consume (`RecipeDetailSections`
 * on screen, `PrintRecipeArticle` in print packets). Pure — no React —
 * so both the detail panel and the cookbook-print builder share one
 * conversion and the unit-test runner can exercise it directly.
 */

import { pickRecipeMeasure } from '@ordo/constants/recipe-measure'

import type { RecipeDetailIngredientRow, RecipeDetailVariantRow } from '@/api/recipes'
import type { Recipe } from '@/lib/chefs/types'

export type RecipeDetailRecipe = NonNullable<RecipeDetailVariantRow['recipe']>

/**
 * Convert a RecipeIngredient row's mixed-quantity columns into a
 * single chef-recipe quantity. Schema-side, exactly one of
 * weight / volume / size / quantity is typically populated per row.
 *
 * Slot precedence is the monorepo-wide order owned by
 * `@ordo/constants/recipe-measure` (weight > volume > quantity > size).
 * This used to rank size ABOVE quantity, so a line carrying both would
 * have shown the chef a different amount from the one the buy list bought
 * and the order snapshot froze. Null when no measure is populated — the
 * row renders name-only rather than a fabricated `0 unit`.
 */
export function pickIngredientQuantity(ri: RecipeDetailIngredientRow): { quantity: number; unit: string } | null {
  const picked = pickRecipeMeasure(ri)
  return picked === null ? null : { quantity: picked.value, unit: picked.unit }
}

export function toChefRecipe(recipe: RecipeDetailRecipe): Recipe {
  return {
    id: recipe.id,
    baseYield: recipe.yieldValue ?? 1,
    yieldUnit: recipe.yieldUnit ?? 'servings',
    ingredients: recipe.ingredients.map((ri) => {
      const qty = pickIngredientQuantity(ri)
      return {
        id: ri.id,
        name: ri.ingredient.name,
        quantity: qty?.quantity ?? null,
        unit: qty?.unit ?? null,
        quantityBasis: ri.quantityBasis,
        brand: ri.brand,
      }
    }),
    steps: recipe.instructions.map((instruction) => ({
      id: instruction.id,
      text: instruction.body,
      ingredientRefs: instruction.ingredientRefs,
    })),
    haccp: recipe.haccpPoints.map((point) => ({
      id: point.id,
      text: haccpPointText(point),
    })),
    tips: recipe.tips.map((tip) => ({ id: tip.id, text: tip.body })),
  }
}

function haccpPointText(point: {
  title: string
  body: string | null
  criticalLimit: string | null
  correctiveAction: string | null
}): string {
  return [
    point.title,
    point.body,
    point.criticalLimit ? `Critical limit: ${point.criticalLimit}` : null,
    point.correctiveAction ? `Corrective action: ${point.correctiveAction}` : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' ')
}
